import { Particle } from '../ParticleLayer';

// GLOBAL SPRITE CACHE FOR STEAL EFFECT
const stealSpriteCache = new Map<string, HTMLCanvasElement>();

function getStealSteamSprite(color: string, size: number): HTMLCanvasElement {
    const qSize = Math.max(1, Math.round(size));
    const key = `steam-${color}-${qSize}`;
    let sprite = stealSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        sprite.width = qSize * 2;
        sprite.height = qSize * 2;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = qSize;
            const centerY = qSize;

            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);

            const grad = sCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, qSize);
            grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.6)`);
            grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            sCtx.fillStyle = grad;
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, qSize, 0, Math.PI * 2);
            sCtx.fill();
        }
        stealSpriteCache.set(key, sprite);
    }
    return sprite;
}

function getStealEyeSprite(): HTMLCanvasElement {
    const key = `eyes`;
    let sprite = stealSpriteCache.get(key);

    if (!sprite) {
        sprite = document.createElement('canvas');
        sprite.width = 60;
        sprite.height = 60;
        const sCtx = sprite.getContext('2d');
        if (sCtx) {
            const centerX = 30;
            const centerY = 30;

            sCtx.shadowBlur = 15;
            sCtx.shadowColor = 'rgba(255, 255, 100, 0.6)';
            sCtx.fillStyle = 'black';
            sCtx.beginPath();
            sCtx.arc(centerX, centerY, 10, 0, Math.PI * 2);
            sCtx.fill();
            sCtx.shadowBlur = 0;

            sCtx.fillStyle = '#ffffcc';
            sCtx.beginPath();
            sCtx.ellipse(centerX - 4, centerY, 2, 3, 0, 0, Math.PI * 2);
            sCtx.ellipse(centerX + 4, centerY, 2, 3, 0, 0, Math.PI * 2);
            sCtx.fill();
        }
        stealSpriteCache.set(key, sprite);
    }
    return sprite;
}

export type StealEffectData = {
    sourceX: number;
    sourceY: number;
    targetX: number;
    targetY: number;

    // Cape Cloth Simulation
    capeNodes: {
        x: number;
        y: number;
        vx: number;
        vy: number;
        ox: number; // Original relative X
        oy: number; // Original relative Y
    }[][]; // Grid of nodes

    // Footprints
    footprints: {
        x: number;
        y: number;
        angle: number;
        life: number;
        isRight: boolean; // Left or right foot
    }[];

    // Sand/Dust Particles
    sandParticles: {
        x: number;
        y: number;
        vx: number;
        vy: number;
        life: number;
        color: string;
        size: number;
        type: 'sand' | 'dust' | 'steam'; // Differentiate types
    }[];

    waddlePhase: number;
    currentX: number;
    currentY: number;
    stepTimer: number;

    // Pathing
    startPos: { x: number, y: number };
    endPos: { x: number, y: number };
}

export type StealEffect = {
    kind: 'steal';
    t: number;
    data: StealEffectData;
}

export function createStealEffect(sx: number, sy: number, tx: number, ty: number): StealEffect {
    // Initialize Cape Grid (Width x Height)
    const cols = 10;
    const rows = 8; // Shorter cape
    const capeNodes = [];

    // Determine Path (Hand to Hand) - Deeper Run
    const winW = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const winH = typeof window !== 'undefined' ? window.innerHeight : 1080;

    let startPos = { x: sx, y: sy };
    let endPos = { x: tx, y: ty };

    // Push start/end points further out for "deeper" run
    if (sy > ty) {
        // Player -> Opponent
        startPos = { x: winW / 2, y: winH + 50 }; // Start off-screen bottom
        endPos = { x: winW / 2, y: -50 };         // End off-screen top
    } else {
        // Opponent -> Player
        startPos = { x: winW / 2, y: -50 };
        endPos = { x: winW / 2, y: winH + 50 };
    }

    for (let r = 0; r < rows; r++) {
        const row = [];
        for (let c = 0; c < cols; c++) {
            // Rounder Shape Logic
            let widthMod = 1.0;
            if (r < 2) widthMod = 0.6 + (r * 0.2); // Neck taper

            // Round bottom corners
            if (r > rows - 3) {
                const distFromCenter = Math.abs(c - cols / 2);
                if (distFromCenter > cols / 2 - 2) {
                    widthMod *= 0.7;
                }
            }

            row.push({
                x: startPos.x,
                y: startPos.y,
                vx: 0,
                vy: 0,
                ox: (c - cols / 2) * 9 * widthMod, // Wider (was 6)
                oy: r * 4 // Even shorter vertical spacing (was 5)
            });
        }
        capeNodes.push(row);
    }

    return {
        kind: 'steal',
        t: 0,
        data: {
            sourceX: sx,
            sourceY: sy,
            targetX: tx,
            targetY: ty,
            capeNodes,
            footprints: [],
            sandParticles: [],
            waddlePhase: 0,
            currentX: startPos.x,
            currentY: startPos.y,
            stepTimer: 0,
            startPos,
            endPos
        }
    };
}

export function renderStealEffect(
    effect: StealEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean {
    const TOTAL_TIME = 8000;
    const df = (effect as any)._df ?? 1;
    const t = effect.t;
    const data = effect.data;

    if (t > TOTAL_TIME) return false;

    // Movement Logic (Start -> End -> Start)
    // 8.0s Total:
    // 0 -> 3.5s: Go to target
    // 3.5s -> 4.5s: Grab/Interact
    // 4.5s -> 8.0s: Return
    const phase1Dur = 3500;
    const grabDur = 1000;
    const phase2Dur = 3500;

    let targetX = data.startPos.x;
    let targetY = data.startPos.y;
    let progress = 0;
    let moving = false;
    let velocityX = 0;
    let velocityY = 0;

    if (t < phase1Dur) {
        // Going to target
        progress = t / phase1Dur;
        progress = progress * progress * (3 - 2 * progress);
        const nextX = data.startPos.x + (data.endPos.x - data.startPos.x) * progress;
        const nextY = data.startPos.y + (data.endPos.y - data.startPos.y) * progress;

        velocityX = nextX - data.currentX;
        velocityY = nextY - data.currentY;

        targetX = nextX;
        targetY = nextY;
        moving = true;
    } else if (t < phase1Dur + grabDur) {
        // Grabbing
        targetX = data.endPos.x;
        targetY = data.endPos.y;
        moving = false;
    } else {
        // Returning
        progress = (t - phase1Dur - grabDur) / phase2Dur;
        progress = progress * progress * (3 - 2 * progress);
        const nextX = data.endPos.x + (data.startPos.x - data.endPos.x) * progress;
        const nextY = data.endPos.y + (data.startPos.y - data.endPos.y) * progress;

        velocityX = nextX - data.currentX;
        velocityY = nextY - data.currentY;

        targetX = nextX;
        targetY = nextY;
        moving = true;
    }

    // Waddling Motion
    if (moving) {
        data.waddlePhase += 0.12 * df;
    } else {
        data.waddlePhase += 0.05 * df;
    }

    const waddleOffset = Math.sin(data.waddlePhase) * 20;
    const hopOffset = Math.abs(Math.cos(data.waddlePhase)) * 12;

    data.currentX = targetX + waddleOffset;
    data.currentY = targetY - hopOffset;

    // --- Spawn Steam Trace ---
    // Disease-like: cloudy, volumetric, pale purple
    // Spawn multiple overlapping particles per frame
    for (let i = 0; i < Math.round(3 * df); i++) {
        const steamSize = 15 + Math.random() * 20;
        // Pale purple shades
        const colors = ['#d8b4fe', '#e9d5ff', '#f3e8ff'];
        const color = colors[Math.floor(Math.random() * colors.length)];

        data.sandParticles.push({
            x: data.currentX + (Math.random() - 0.5) * 40, // Wide spawn area
            y: data.currentY + (Math.random() - 0.5) * 30,
            vx: velocityX * 0.05 + (Math.random() - 0.5) * 0.3, // Minimal velocity inheritance
            vy: velocityY * 0.05 - 0.1 - Math.random() * 0.2,   // Slow drift up
            life: 600 + Math.random() * 600, // Short-ish life
            color: color,
            size: steamSize,
            type: 'steam'
        });
    }

    // Footprints Logic
    data.stepTimer += 16.7 * df;
    if (moving && data.stepTimer > 150) {
        data.stepTimer = 0;
        const isRight = Math.sin(data.waddlePhase) > 0;
        const fpX = data.currentX + (isRight ? 20 : -20); // Wider stance
        const fpY = data.currentY + 35; // Adjusted for shorter cape

        let moveAngle = Math.atan2(data.endPos.y - data.startPos.y, data.endPos.x - data.startPos.x);
        if (t > phase1Dur + grabDur) {
            moveAngle = Math.atan2(data.startPos.y - data.endPos.y, data.startPos.x - data.endPos.x);
        }

        data.footprints.push({
            x: fpX,
            y: fpY,
            angle: moveAngle + Math.PI / 2,
            life: 3000,
            isRight
        });

        // Sand Splashes
        for (let i = 0; i < 5; i++) {
            const splashAngle = Math.random() * Math.PI * 2;
            const speed = 0.5 + Math.random() * 2;
            data.sandParticles.push({
                x: fpX + (Math.random() - 0.5) * 5,
                y: fpY,
                vx: Math.cos(splashAngle) * speed,
                vy: Math.sin(splashAngle) * speed - 1.5,
                life: 800 + Math.random() * 400,
                color: Math.random() > 0.5 ? '#d4b483' : '#e6ccb2',
                size: 1 + Math.random() * 2,
                type: 'sand'
            });
        }
    }

    // Update Cape Nodes (Cloth Simulation)
    const gravity = 0.4;
    const stiffness = 0.3;

    data.capeNodes.forEach((row, r) => {
        row.forEach((node, c) => {
            if (r === 0) {
                node.x = data.currentX + node.ox;
                node.y = data.currentY + node.oy;
                return;
            }

            node.vx *= Math.pow(0.92, df);
            node.vy *= Math.pow(0.92, df);
            node.vy += gravity * df;

            let moveX = 0;
            let moveY = 0;
            if (t < phase1Dur) {
                moveX = (data.endPos.x - data.startPos.x) / phase1Dur;
                moveY = (data.endPos.y - data.startPos.y) / phase1Dur;
            } else if (t > phase1Dur + grabDur) {
                moveX = (data.startPos.x - data.endPos.x) / phase2Dur;
                moveY = (data.startPos.y - data.endPos.y) / phase2Dur;
            }

            node.vx -= moveX * 50 * (r / 10);
            node.vy -= moveY * 50 * (r / 10);

            node.vx += (Math.random() - 0.5) * 0.8 * df;
            node.vy += (Math.random() - 0.5) * 0.5 * df;

            node.x += node.vx * df;
            node.y += node.vy * df;

            const above = data.capeNodes[r - 1][c];
            const dx = node.x - above.x;
            const dy = node.y - above.y;
            const dist = Math.hypot(dx, dy);
            const targetDist = 5;

            if (dist > targetDist) {
                const ratio = (dist - targetDist) / dist;
                const correctionX = dx * ratio * stiffness;
                const correctionY = dy * ratio * stiffness;

                node.x -= correctionX;
                node.y -= correctionY;
                node.vx -= correctionX * 0.5;
                node.vy -= correctionY * 0.5;
            }
        });
    });

    // Update Particles
    for (let i = data.sandParticles.length - 1; i >= 0; i--) {
        const p = data.sandParticles[i];
        p.life -= 16.7 * df;
        if (p.life <= 0) {
            data.sandParticles[i] = data.sandParticles[data.sandParticles.length - 1];
            data.sandParticles.pop();
            continue;
        }
        p.x += p.vx * df;
        p.y += p.vy * df;

        if (p.type === 'sand') {
            p.vy += 0.15 * df;
        } else if (p.type === 'dust') {
            p.size += 0.15 * df;
            p.vy -= 0.02 * df;
        } else if (p.type === 'steam') {
            p.size += 0.2 * df;
            p.vx *= Math.pow(0.85, df);
            p.vy -= 0.01 * df;
        }
    }

    // --- RENDER ---

    // 1. Footprints (Clearer, Darker, Bigger)
    ctx.save();
    for (let i = data.footprints.length - 1; i >= 0; i--) {
        const fp = data.footprints[i];
        fp.life -= 16.7 * df;
        if (fp.life <= 0) {
            data.footprints[i] = data.footprints[data.footprints.length - 1];
            data.footprints.pop();
            continue;
        }

        // Start opaque, fade out late
        let alpha = 1.0;
        if (fp.life < 1000) alpha = fp.life / 1000;
        ctx.globalAlpha = alpha;

        ctx.translate(fp.x, fp.y);
        ctx.rotate(fp.angle);

        // Scale up by 60% (was 20%)
        ctx.scale(1.6, 1.6);

        ctx.fillStyle = '#2a1d15'; // Very Dark Brown/Black

        // Main Foot
        ctx.beginPath();
        ctx.moveTo(-2, 4);
        ctx.bezierCurveTo(-5, 4, -5, -4, -2, -4);
        ctx.bezierCurveTo(4, -5, 4, 5, -2, 4);
        ctx.fill();

        // Toes
        for (let j = 0; j < 5; j++) {
            ctx.beginPath();
            const tx = (j - 2) * 1.8;
            const ty = -6 - Math.abs(j - 2) * 0.3;
            const size = j === 0 ? 1.2 : 0.8;
            ctx.arc(tx, ty, size, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.scale(1 / 1.6, 1 / 1.6); // Reset scale
        ctx.rotate(-fp.angle);
        ctx.translate(-fp.x, -fp.y);
    }
    ctx.restore();

    // 2. Particles (Sand & Dust & Steam)
    // Render Steam first (behind)
    data.sandParticles.forEach(p => {
        if (p.type !== 'steam') return;
        ctx.save();
        // Steam visual: Pale purple, cloudy, disease-like
        const alpha = Math.min(0.5, p.life / 400);
        ctx.globalAlpha = alpha;

        const steamSprite = getStealSteamSprite(p.color, p.size);
        ctx.drawImage(steamSprite, p.x - p.size, p.y - p.size);

        ctx.restore();
    });

    // Render Sand/Dust
    data.sandParticles.forEach(p => {
        if (p.type === 'steam') return;
        ctx.save();
        if (p.type === 'dust') {
            ctx.globalAlpha = (p.life / 1200) * 0.4;
        } else {
            ctx.globalAlpha = p.life / 800;
        }

        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    });

    // 3. The Cape (Wider, Shorter)
    ctx.save();

    // Draw vertical strips
    for (let c = 0; c < data.capeNodes[0].length - 1; c++) {
        ctx.beginPath();
        const first = data.capeNodes[0][c];
        ctx.moveTo(first.x, first.y);

        for (let r = 1; r < data.capeNodes.length; r++) {
            const node = data.capeNodes[r][c];
            ctx.lineTo(node.x, node.y);
        }

        const lastBottom = data.capeNodes[data.capeNodes.length - 1][c + 1];
        ctx.lineTo(lastBottom.x, lastBottom.y);

        for (let r = data.capeNodes.length - 2; r >= 0; r--) {
            const node = data.capeNodes[r][c + 1];
            ctx.lineTo(node.x, node.y);
        }

        ctx.closePath();

        const noise = Math.random() * 0.1;
        const alpha = 0.25 + Math.sin(t * 0.002 + c * 0.5) * 0.1 + noise;
        const grey = 200 + Math.random() * 30;
        ctx.fillStyle = `rgba(${grey}, ${grey}, ${grey}, ${alpha})`;
        ctx.fill();

        ctx.strokeStyle = `rgba(200, 200, 200, 0.1)`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
    }

    // 4. Eyes
    const headX = data.currentX;
    const headY = data.currentY + 15;

    ctx.globalAlpha = 0.8;
    const eyeSprite = getStealEyeSprite();
    ctx.drawImage(eyeSprite, headX - 30, headY - 30);

    ctx.restore();

    return true;
}
